import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import HeaderWithTooltip from '@/components/UI/TableListEditable/HeaderWithTooltip/HeaderWithTooltip';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { VEHICLE_STATUS_COMMON_COUNT_COLUMNS, VEHICLE_STATUS_OVERVIEW_COLUMNS } from '@/helpers/constants';
import { parseObjectNegativeValuesToBlank } from '@/helpers/utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import { getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { getSubCampaignFeedback } from '@/redux/SubCampaign/subCampaign.reducer';
import { FeedbackBlockingLocation, Language, SubCampaignFeedback } from '@/types';
import type { ColumnConfig } from '@/types/TableListEditable';
import styles from './Feedback.module.scss';

interface FeedbackVehiclesTableProps {
  headerLanguage: Language;
  subCampaignFeedback: SubCampaignFeedback;
  campaignNumber: string;
}

type FeedbackVehiclesRow = {
  key: string;
  fourthLevelRow?: boolean;
} & Partial<Record<string, string | number | null>>;

const FeedbackVehiclesTable: React.FC<FeedbackVehiclesTableProps> = (props) => {
  const { headerLanguage, subCampaignFeedback, campaignNumber } = props;

  const { t } = useTranslation();
  const lang = useAppLanguage();

  const feedbackVehiclesColumns = useMemo<ColumnConfig[]>(() => [
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.CAMPAIGN, title: t('feedback.campaignNumber'), flex: 1.5 },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.DYNAMIC, title: t('plantFeedback.dynamic') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.BLOCKING_LOCATION, title: t('plantFeedback.blockingLocation'), flex: 1.5 },
    {
      name: VEHICLE_STATUS_OVERVIEW_COLUMNS.ESTIMATED_CARS,
      title: t('plantFeedback.estimatedCars'),
      headerComponent: HeaderWithTooltip,
      headerComponentParams: { tooltipText: t('plantFeedback.estimatedCarsTooltip'), isWarningTooltip: false },
    },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.VALIDATED_CARS, title: t('plantFeedback.validatedCars') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.NO_REWORK, title: t('plantFeedback.noRework') },
    {
      name: VEHICLE_STATUS_OVERVIEW_COLUMNS.REWORK_NOT_STARTED,
      title: t('plantFeedback.reworkNotStarted'),
      headerComponent: HeaderWithTooltip,
      headerComponentParams: { tooltipText: t('plantFeedback.reworkNotStartedTooltip'), isWarningTooltip: false },
    },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.REWORK_OPEN, title: t('plantFeedback.reworkOpen') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.REWORK_FINISHED, title: t('plantFeedback.reworkFinished') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.OK_WITHOUT_REWORK, title: t('feedback.withoutRework') },
    { name: VEHICLE_STATUS_OVERVIEW_COLUMNS.REWORK_IN_FIELD, title: t('plantFeedback.reworkInField') },
  ], [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const tableData = useMemo<FeedbackVehiclesRow[]>(() => {
    const campaigns = subCampaignFeedback?.campaigns ?? [];

    const rows: FeedbackVehiclesRow[] = [];

    campaigns
      .filter((campaign) => !!campaignNumber && campaign?.campaignManagerEntry?.campaignNumber === campaignNumber)
      .forEach((campaign) => {
        const { preliminaryInformation } = campaign;
        const blockingLocations = preliminaryInformation?.blockingLocations
          ?? (campaign as Record<string, unknown>)?.blockingLocations ?? [];

        rows.push({
          key: campaignNumber,
          [VEHICLE_STATUS_OVERVIEW_COLUMNS.CAMPAIGN]: campaignNumber,
          [VEHICLE_STATUS_OVERVIEW_COLUMNS.DYNAMIC]: preliminaryInformation?.dynamic
            ? t('plantFeedback.dynamicYes', { lng: headerLanguage?.toLowerCase() })
            : t('plantFeedback.dynamicNo', { lng: headerLanguage?.toLowerCase() }),
          [VEHICLE_STATUS_OVERVIEW_COLUMNS.ESTIMATED_CARS]: preliminaryInformation?.estimatedCars,
          ...parseObjectNegativeValuesToBlank(VEHICLE_STATUS_COMMON_COUNT_COLUMNS, preliminaryInformation),
        });

        (blockingLocations as FeedbackBlockingLocation[]).forEach((location) => {
          rows.push({
            key: `${campaignNumber}_${location.location}`,
            [VEHICLE_STATUS_OVERVIEW_COLUMNS.BLOCKING_LOCATION]: location.location,
            fourthLevelRow: true,
            ...parseObjectNegativeValuesToBlank(VEHICLE_STATUS_COMMON_COUNT_COLUMNS, location),
          });
        });
      });

    return rows;
  }, [subCampaignFeedback, headerLanguage, lang, campaignNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  if (tableData.length === 0) return null;

  return (
    <div id="feedback-vehicles-table" data-testid="feedback-vehicles-table">
      <div className={styles.TitleSection}>
        <h3>{t('feedback.areaTitle')}</h3>
      </div>

      <div className={`w-100 ${styles.FeedbackVehiclesTable}`}>
        <TableListEditableNew
          list={tableData}
          columns={feedbackVehiclesColumns}
          columnId="key"
          isPaginated={false}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  subCampaignFeedback: getSubCampaignFeedback(state),
});

export default connect(mapStateToProps)(FeedbackVehiclesTable);
